// Espejo zod EXACTO de pipeline/flyer_schema.py (Pydantic). Fuente única de
// verdad: pipeline/flyer_schema.py — ver pipeline/CONTRATO_RENDERER.md
// sección 4. Cualquier divergencia entre este archivo y flyer_schema.py es un
// bug.
//
// `format.width`/`format.height` son `computed_field` en Pydantic: no forman
// parte del JSON de entrada (por eso `FlyerFormatSchema` solo valida
// `aspect`, igual que el modelo de entrada de Pydantic). El renderer deriva
// el tamaño de canvas real con `FLYER_DIMENSIONS`/`getFlyerDimensions`, no
// leyendo un campo `width`/`height` que podría venir o no en el props.json.
import { z } from "zod";
// Fuente única del catálogo de iconos: scripts/gen-icons.mjs genera ICON_IDS
// (y TABLER_ICONS) desde scripts/catalogs/tabler-icons.txt, y el MISMO script
// emite pipeline/catalogs/flyer_icons.py — el catálogo del director (Python) y
// el validado por zod nunca divergen.
// Extensión .ts explícita (como ./types.ts en types.test.ts): el runner
// `node --test` resuelve solo con extensión; tsc/remotion la aceptan igual.
import { ICON_IDS } from "./catalogs/tabler.generated.ts";

export const HexColorSchema = z
  .string()
  .regex(/^(#[0-9a-fA-F]{6}|\$brand\.[a-z]+)$/);
export type HexColor = z.infer<typeof HexColorSchema>;

// M2: grid de 9 anclajes (3x3) -- se conservan los 3 originales tal cual
// (AV-2). El mapeo de cada anchor a su traslación vive en un único lugar del
// renderer (lib/positioning.ts::ANCHOR_TRANSLATE).
const AnchorSchema = z.enum([
  "center",
  "top_left",
  "top_center",
  "top_right",
  "center_left",
  "center_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
]);
export type Anchor = z.infer<typeof AnchorSchema>;

export const PositionSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  anchor: AnchorSchema.default("center"),
});
export type Position = z.infer<typeof PositionSchema>;

const BaseLayerSchema = z.object({
  id: z.string(),
  position: PositionSchema,
  scale: z.number().min(0.3).max(1.6).default(1.0),
  z: z.number().int().default(0),
  // Rotación (grados) alrededor del centro de la capa — espejo de
  // flyer_schema.py::_BaseLayer.rotation_deg. Se aplica en lib/positioning.ts.
  rotation_deg: z.number().min(-180).max(180).default(0),
});

const TextStyleSchema = z.enum([
  "headline",
  "subtitle",
  "body",
  "price",
  "cta",
  "caption",
]);
export type TextStyleName = z.infer<typeof TextStyleSchema>;

// M2 (docs/hueco-vocabulario.md #6): caja/píldora opcional detrás de un
// TextLayer -- fill/borde/radio/padding en el mismo wrapper del texto, sin
// acoplar una capa `shape` aparte. Padding en `em` (relativo al fontSize real
// de la capa). Espejo EXACTO de pipeline/flyer_schema.py::TextBox.
export const TextBoxSchema = z.object({
  fill: HexColorSchema,
  fill_opacity: z.number().min(0.0).max(1.0).default(1.0),
  border_color: HexColorSchema.nullish(),
  border_opacity: z.number().min(0.0).max(1.0).default(1.0),
  border_width: z.number().min(0.0).max(8.0).default(0.0),
  // px literal (no fracción, a diferencia de ShapeLayer.corner_radius): ver
  // el comentario equivalente en flyer_schema.py::TextBox.
  corner_radius: z.number().min(0.0).max(999.0).default(0.0),
  padding_x_em: z.number().min(0.0).max(3.0).default(0.0),
  padding_y_em: z.number().min(0.0).max(3.0).default(0.0),
});
export type TextBox = z.infer<typeof TextBoxSchema>;

export const TextLayerSchema = BaseLayerSchema.extend({
  type: z.literal("text"),
  content: z.string().min(1).max(500),
  text_style: TextStyleSchema,
  max_width: z.number().min(0.3).max(0.95).default(0.84),
  color: HexColorSchema.nullish(),
  // M2 (docs/hueco-vocabulario.md): vocabulario visual rico -- TODOS
  // opcionales/con default que reproduce EXACTO el render de hoy (AV-2).
  align: z.enum(["left", "center", "right"]).default("center"),
  letter_spacing_em: z.number().min(0.0).max(0.5).default(0.0),
  text_transform: z.enum(["none", "uppercase"]).default("none"),
  // Se ignora en render (queda redonda) si la familia efectiva no tiene un
  // corte de itálica real -- ver fontAllowlist.ts::familiaTieneItalica (AV-6).
  italic: z.boolean().default(false),
  // Ausente => el peso fijo del text_style (theme.ts::textStyles).
  font_weight: z.number().int().min(300).max(900).multipleOf(100).nullish(),
  // Ausente => el lineHeight fijo del text_style.
  line_height: z.number().min(0.8).max(2.0).nullish(),
  // IM-1: multiplica el tamaño base ANTES de fitFontSize (theme.ts), nunca
  // reemplaza `font_size` directo. 1.0 = comportamiento actual exacto.
  size_factor: z.number().min(0.6).max(2.0).default(1.0),
  // "none" = comportamiento actual exacto (TextLayer.tsx no pinta ninguna
  // sombra hoy).
  text_shadow: z.enum(["none", "soft", "strong"]).default("none"),
  box: TextBoxSchema.nullish(),
});
export type TextLayer = z.infer<typeof TextLayerSchema>;

export const LogoLayerSchema = BaseLayerSchema.extend({
  type: z.literal("logo"),
  asset_src: z.string().nullish(),
  show_name: z.boolean().default(true),
});
export type LogoLayer = z.infer<typeof LogoLayerSchema>;

export const ImageLayerSchema = BaseLayerSchema.extend({
  type: z.literal("image"),
  asset_src: z.string().nullish(),
  fit: z.enum(["cover", "contain"]).default("cover"),
  width: z.number().min(0.1).max(1.0).default(0.6),
  caption: z.string().max(60).nullish(),
});
export type ImageLayer = z.infer<typeof ImageLayerSchema>;

// Catálogo curado a mano (placeholder de M1) — ver
// pipeline/catalogs/flyer_illustrations.py / CONTRATO_RENDERER.md sección 5.
export const ILLUSTRATION_IDS = [
  "celebration",
  "shopping",
  "discount",
  "delivery",
  "marketing",
  "growth",
  "team",
  "event",
  "sale",
  "reviews",
  "location",
  "notify",
] as const;
export const IllustrationIdSchema = z.enum(ILLUSTRATION_IDS);
export type IllustrationId = z.infer<typeof IllustrationIdSchema>;

export const IllustrationLayerSchema = BaseLayerSchema.extend({
  type: z.literal("illustration"),
  illustration_id: IllustrationIdSchema,
});
export type IllustrationLayer = z.infer<typeof IllustrationLayerSchema>;

// ICON_IDS se importa arriba desde el catálogo GENERADO (tabler.generated.ts).
export { ICON_IDS };
export const IconIdSchema = z.enum(ICON_IDS);
export type IconId = z.infer<typeof IconIdSchema>;

export const IconLayerSchema = BaseLayerSchema.extend({
  type: z.literal("icon"),
  icon_id: IconIdSchema,
  // M2 (docs/hueco-vocabulario.md #9): fracción del ANCHO del lienzo.
  // Ausente => 200px fijos EXACTOS como hoy (IconLayer.tsx) -- AV-2.
  size: z.number().min(0.03).max(0.6).nullish(),
});
export type IconLayer = z.infer<typeof IconLayerSchema>;

export const DonutChartLayerSchema = BaseLayerSchema.extend({
  type: z.literal("chart_donut"),
  value: z.number().min(1).max(100),
  label: z.string().max(30),
  secondary_label: z.string().max(30),
});
export type DonutChartLayer = z.infer<typeof DonutChartLayerSchema>;

export const StarsLayerSchema = BaseLayerSchema.extend({
  type: z.literal("stars"),
  rating: z.number().min(1).max(5),
});
export type StarsLayer = z.infer<typeof StarsLayerSchema>;

export const ButtonLayerSchema = BaseLayerSchema.extend({
  type: z.literal("button"),
  content: z.string().min(1).max(40),
  // M4.1: overrides opcionales de color. Ausentes => brand.secondary /
  // brand.text como siempre (AV-2). Paridad exacta con flyer_schema.py.
  fill: HexColorSchema.nullish(),
  text_color: HexColorSchema.nullish(),
});
export type ButtonLayer = z.infer<typeof ButtonLayerSchema>;

// M2 (docs/hueco-vocabulario.md #7): degradado lineal de 2-4 paradas -- UN
// solo modelo compartido por ShapeLayer.gradient e ImageBackground.scrim.
// `angle_deg` en convención CSS (0=hacia arriba, 180=hacia abajo), igual que
// GradientBackgroundSchema.angle_deg. Espejo EXACTO de
// pipeline/flyer_schema.py::GradientStop/GradientSpec.
export const GradientStopSchema = z.object({
  color: HexColorSchema,
  opacity: z.number().min(0.0).max(1.0).default(1.0),
  at: z.number().min(0.0).max(1.0),
});
export type GradientStop = z.infer<typeof GradientStopSchema>;

export const GradientSpecSchema = z.object({
  angle_deg: z.number().min(0).max(360).default(180),
  stops: z.array(GradientStopSchema).min(2).max(4),
});
export type GradientSpec = z.infer<typeof GradientSpecSchema>;

export const ShapeLayerSchema = BaseLayerSchema.extend({
  type: z.literal("shape"),
  shape: z.enum(["rectangle", "circle", "line"]),
  // min 0.001 (no 0.02): una "line" (regla/divisor) es naturalmente delgada.
  // max 1.5 (no 1.0): banda diagonal a sangre que sobrepasa el borde al rotar.
  // Paridad exacta con flyer_schema.py.
  width: z.number().min(0.001).max(1.5).default(0.2),
  height: z.number().min(0.001).max(1.5).default(0.2),
  color: HexColorSchema,
  corner_radius: z.number().min(0.0).max(0.5).default(0.0),
  opacity: z.number().min(0.0).max(1.0).default(1.0),
  // M2c: borde opcional (píldoras/cajas con contorno translúcido). Ausente =>
  // sin borde, render idéntico a antes. Paridad exacta con flyer_schema.py.
  border_color: HexColorSchema.nullish(),
  border_opacity: z.number().min(0.0).max(1.0).default(1.0),
  border_width: z.number().min(0.0).max(8.0).default(0.0),
  shadow: z.boolean().default(false),
  // M2 (IM-6): opcional, GANA sobre `color` si está presente; `color` NUNCA
  // se renombra ni se quita -- sigue siendo el fallback sólido sin degradado.
  gradient: GradientSpecSchema.nullish(),
});
export type ShapeLayer = z.infer<typeof ShapeLayerSchema>;

export const LayerSchema = z.discriminatedUnion("type", [
  TextLayerSchema,
  LogoLayerSchema,
  ImageLayerSchema,
  IllustrationLayerSchema,
  IconLayerSchema,
  DonutChartLayerSchema,
  StarsLayerSchema,
  ButtonLayerSchema,
  ShapeLayerSchema,
]);
export type Layer = z.infer<typeof LayerSchema>;

export const ColorBackgroundSchema = z.object({
  type: z.literal("color"),
  color: HexColorSchema,
});
export type ColorBackground = z.infer<typeof ColorBackgroundSchema>;

export const GradientBackgroundSchema = z.object({
  type: z.literal("gradient"),
  from_color: HexColorSchema,
  to_color: HexColorSchema,
  angle_deg: z.number().min(0).max(360).default(135),
});
export type GradientBackground = z.infer<typeof GradientBackgroundSchema>;

export const ImageBackgroundSchema = z.object({
  type: z.literal("image"),
  asset_src: z.string().nullish(),
  fallback_color: HexColorSchema.default("$brand.background"),
  // M2: overlay opcional (2-4 paradas, mismo modelo que ShapeLayer.gradient)
  // sobre la foto. Ausente => pinta exactamente lo de hoy (sin overlay).
  scrim: GradientSpecSchema.nullish(),
});
export type ImageBackground = z.infer<typeof ImageBackgroundSchema>;

export const BackgroundSchema = z.discriminatedUnion("type", [
  ColorBackgroundSchema,
  GradientBackgroundSchema,
  ImageBackgroundSchema,
]);
export type Background = z.infer<typeof BackgroundSchema>;

// Colores de marca: hex estricto, SIN referencia `$brand.*` (serían los
// propios valores referenciados).
const StrictHexSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const BrandColorsSchema = z.object({
  primary: StrictHexSchema,
  secondary: StrictHexSchema,
  background: StrictHexSchema,
  text: StrictHexSchema,
});
export type BrandColors = z.infer<typeof BrandColorsSchema>;

export const BrandFontsSchema = z.object({
  heading: z.string().max(60).nullish(),
  body: z.string().max(60).nullish(),
});
export type BrandFonts = z.infer<typeof BrandFontsSchema>;

export const BrandSchema = z.object({
  name: z.string().max(40).nullish(),
  colors: BrandColorsSchema,
  fonts: BrandFontsSchema.nullish(),
});
export type Brand = z.infer<typeof BrandSchema>;

export const FLYER_DIMENSIONS: Record<
  "1:1" | "4:5" | "9:16" | "16:9",
  { width: number; height: number }
> = {
  "1:1": { width: 1080, height: 1080 },
  "4:5": { width: 1080, height: 1350 },
  "9:16": { width: 1080, height: 1920 },
  "16:9": { width: 1920, height: 1080 },
};

export const FlyerFormatSchema = z.object({
  aspect: z.enum(["1:1", "4:5", "9:16", "16:9"]),
});
export type FlyerFormat = z.infer<typeof FlyerFormatSchema>;

export const getFlyerDimensions = (
  aspect: FlyerFormat["aspect"],
): { width: number; height: number } => FLYER_DIMENSIONS[aspect];

export const FlyerDocumentSchema = z.object({
  format: FlyerFormatSchema,
  brand: BrandSchema.nullish(),
  background: BackgroundSchema,
  // max 24 (no 16): espejo de flyer_schema.py — un split-panel con collage
  // necesita ~20 capas.
  layers: z.array(LayerSchema).min(1).max(24),
});
export type FlyerDocument = z.infer<typeof FlyerDocumentSchema>;
